import { tr } from 'i18n';
import VehicleNumberInput from 'components/Forms/VehicleNumberInput/VehicleNumberInput.tsx';
import { normalizeVehicleNumber } from 'service/VehicleNumberService';
import DriverBlockDialog from '../DriverBlockDialog/DriverBlockDialog.tsx';
import DriverInnDialog from '../DriverInnDialog/DriverInnDialog.tsx';
import DriverAssignmentActions from '../DriverAssignmentActions/DriverAssignmentActions.tsx';
import DriverBaseActions from '../DriverBaseActions/DriverBaseActions.tsx';
import DriverPinResetDialog from '../DriverPinResetDialog/DriverPinResetDialog.tsx';
import DriverActivityDialog from '../DriverActivityDialog/DriverActivityDialog.tsx';
import { useTranslation as useInterfaceLanguage } from 'react-i18next';
import React, { useEffect, useId, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Pencil, KeyRound, Ban, LockOpen, ContactRound, X, Save, CarFront, Power, LoaderCircle, ChevronDown, ChevronUp, Paperclip, MoreHorizontal } from "lucide-react";
import { hasAccess } from "utils/access";
import {
  updateDriver,
  resetDriverPin,
  blockDriver,
  unblockDriver,
  changeDriverInn,
  setDriverActive,
} from "service/AdminPanelService";
import DriverInfoCard from "components/DriverInfoCard/DriverInfoCard";
import AuditButton from "./AuditButton";
import { getErpErrorMessage } from "service/ErpService";
import s from "../Management.module.scss";
import layout from './DriverActions.module.scss';

export default function DriverActions({
  driver,
  onChanged = () => {},
  edit = true,
  onDocumentsToggle,
  documentsExpanded,
}) {
  useInterfaceLanguage();
  const user = useSelector((state) => state.user);
  const [editing, setEditing] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const [changingInn, setChangingInn] = useState(false);
  const [resettingPin, setResettingPin] = useState(false);
  const [changingActivity, setChangingActivity] = useState(null);
  const [moreExpanded, setMoreExpanded] = useState(false);
  const [ownDocumentsExpanded, setOwnDocumentsExpanded] = useState(false);
  const actionId = useId();
  const can = (key) => !user?.is_report_only && hasAccess(user, key);
  const canManageDriver = can('drivers_assign') || (driver.employee !== null && (can('drivers_inn') || can('drivers_pin')));
  const baseAssigned = ['top_base', 'chpp', 'private_point'].some(type => driver[type] != null);
  const canManageBase = can(baseAssigned ? 'drivers_base_change' : 'drivers_base_assign') || (baseAssigned && can('drivers_base_remove'));
  const canEditTransport = edit && can('drivers_edit');
  const canManageTransport = canEditTransport || can('drivers_active') || can('drivers_block');
  const hasMoreActions = canManageDriver || canManageBase || canManageTransport;
  const available = ['', 'Свободен', 'Простаивает', 'free', 'idle'].includes(driver.status || '') && !driver.integration_active_load;
  const documentsOpen = onDocumentsToggle ? !!documentsExpanded : ownDocumentsExpanded;
  const [number, setNumber] = useState(normalizeVehicleNumber(driver.vehicle_number || ""));
  const [model, setModel] = useState(driver.vehicle_model || "");
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [active, setActive] = useState(driver.is_active === true);
  useEffect(() => { setActive(driver.is_active === true); }, [driver.id, driver.is_active]);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const run = async (operation, success, fallback) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const result = await operation();
      setEditing(false);
      setBlocking(false);
      setChangingInn(false);
      setResettingPin(false);
      setChangingActivity(null);
      setMessage(typeof success === 'function' ? success(result) : success);
      await onChanged();
    } catch (e) {
      setError(getErpErrorMessage(e, fallback));
    } finally {
      setBusy(false);
      busyRef.current = false;
    }
  };
  return (
    <div className={layout.driverActions} onClick={(event) => event.stopPropagation()}>
      <div className={`${s.actions} ${layout.primaryActions}`}>
        {can('audit') && <AuditButton
          section="drivers"
          objectId={driver.id}
          objectType="tabel.Driver"
        />}
        <button type="button" className={s.button} aria-expanded={documentsOpen}
          aria-controls={!onDocumentsToggle ? `${actionId}-documents` : undefined}
          onClick={() => onDocumentsToggle ? onDocumentsToggle() : setOwnDocumentsExpanded(value => !value)}>
          <Paperclip size={13} aria-hidden="true" />{tr('Документы')}
          {documentsOpen ? <ChevronUp size={13} aria-hidden="true" /> : <ChevronDown size={13} aria-hidden="true" />}
        </button>
        {hasMoreActions && <button type="button" className={s.button} aria-expanded={moreExpanded}
          aria-controls={`${actionId}-more`} onClick={() => setMoreExpanded(value => !value)}>
          <MoreHorizontal size={13} aria-hidden="true" />{tr('Ещё')}
          {moreExpanded ? <ChevronUp size={13} aria-hidden="true" /> : <ChevronDown size={13} aria-hidden="true" />}
        </button>}
      </div>
      {hasMoreActions && <div id={`${actionId}-more`} className={layout.moreActions} hidden={!moreExpanded}>
        {canManageDriver && <section className={layout.actionGroup} aria-label={tr('Водитель')}>
          <h3>{tr('Водитель')}</h3>
          <DriverAssignmentActions driver={driver} onChanged={onChanged} showAvailabilityHint={false} />
          <div className={s.actions}>
            {driver.employee !== null && can('drivers_inn') && <button type="button" className={s.button} disabled={busy}
              onClick={() => { setError(''); setMessage(''); setChangingInn(true); }}>
              <ContactRound size={13} aria-hidden="true" />{tr('Сменить ИНН')}
            </button>}
            {driver.employee !== null && can('drivers_pin') && <button type="button" className={s.button} disabled={busy}
              onClick={() => { setError(''); setMessage(''); setResettingPin(true); }}>
              <KeyRound size={13} aria-hidden="true" />{tr('Сбросить PIN')}
            </button>}
          </div>
        </section>}
        {canManageBase && <section className={layout.actionGroup} aria-label={tr('Работа с базой')}>
          <h3>{tr('Работа с базой')}</h3>
          <DriverBaseActions driver={driver} onChanged={onChanged} showAvailabilityHint={false} />
        </section>}
        {canManageTransport && <section className={layout.actionGroup} aria-label={tr('Транспорт')}>
          <h3>{tr('Транспорт')}</h3>
          <div className={s.actions}>
            {can('drivers_active') && (
              <button
                type="button"
                className={`${s.button} ${s.activityToggle}`}
                data-active={active}
                disabled={busy}
                aria-pressed={active}
                aria-busy={busy}
                aria-label={tr(active ? 'Сделать водителя неактивным' : 'Сделать водителя активным')}
                title={tr(active ? 'Сделать водителя неактивным' : 'Сделать водителя активным')}
                onClick={() => { setError(''); setMessage(''); setChangingActivity(!active); }}
              >
                {busy ? <LoaderCircle size={13} aria-hidden="true" /> : <Power size={13} aria-hidden="true" />}
                {tr(active ? 'Активен' : 'Не активен')}
              </button>
            )}
            {tr(can("drivers_block") && (
              <button
                className={s.danger}
                disabled={busy}
                onClick={() => {
                  if (driver.status === "Заблокирован") run(() => unblockDriver(driver.id), tr("Водитель разблокирован."));
                  else { setError(""); setBlocking(true); }
                }}
              >
                {driver.status === "Заблокирован" ? (
                  <LockOpen size={13} />
                ) : (
                  <Ban size={13} />
                )}{" "}
                {driver.status === "Заблокирован"
                  ? tr("Разблокировать")
                  : tr("Заблокировать")}
              </button>
            ))}
            {canEditTransport && <button type="button" className={s.button} disabled={busy} onClick={() => {
              setNumber(normalizeVehicleNumber(driver.vehicle_number || ''));
              setModel(driver.vehicle_model || '');
              setEditing(true);
              setError('');
            }}><Pencil size={13} aria-hidden="true" />{tr('Редактировать')}</button>}
          </div>
        </section>}
        {!available && (can('drivers_assign') || canManageBase) && <p className={`${s.muted} ${layout.availabilityHint}`}>
          {tr('Смена водителя, работа с базой, доступна, когда транспорт свободен или простаивает.')}
        </p>}
      </div>}
      {!onDocumentsToggle && documentsOpen && <div id={`${actionId}-documents`} className={layout.documents}>
        <DriverInfoCard driver={driver} />
      </div>}
      {tr(error && !editing && !blocking && !changingInn && !resettingPin && changingActivity === null && (
        <p role="alert" className={s.error}>
          {tr(error)}
        </p>
      ))}
      {tr(message && (
        <p role="status" className={s.muted}>
          {tr(message)}
        </p>
      ))}
      {blocking && <DriverBlockDialog name={driver.employee_name || ''} busy={busy} error={error}
        onClose={() => setBlocking(false)} onConfirm={reason => run(() => blockDriver(driver.id, reason), tr("Водитель заблокирован."))} />}
      {changingInn && <DriverInnDialog name={driver.employee_name || ''} currentInn={driver.inn || ''} busy={busy} error={error}
        onClose={() => setChangingInn(false)} onConfirm={(inn, pin) => run(() => changeDriverInn(driver.id, inn, pin), tr('ИНН обновлён. Для входа используйте новый ИНН.'))} />}
      {resettingPin && <DriverPinResetDialog name={driver.employee_name || ''} busy={busy} error={error}
        onClose={() => setResettingPin(false)} onConfirm={pin => run(() => resetDriverPin(driver.id, pin), tr('PIN сброшен. При следующем входе водитель задаст новый код.'))} />}
      {changingActivity !== null && <DriverActivityDialog name={driver.employee_name || driver.vehicle_number || ''} active={changingActivity} busy={busy} error={error}
        onClose={() => setChangingActivity(null)} onConfirm={pin => run(async () => {
          const response = await setDriverActive(driver.id, changingActivity, pin);
          if (typeof response?.data?.is_active !== 'boolean') throw new Error(tr('Не удалось изменить активность водителя.'));
          setActive(response.data.is_active);
          return response;
        }, response => tr(response.data.is_active ? 'Водитель активирован.' : 'Водитель деактивирован.'), tr('Не удалось изменить активность водителя.'))} />}
      {tr(editing && (
        <div className={s.overlay}>
          <form
            className={s.dialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby={`driver-edit-${driver.id}`}
            onSubmit={(e) => {
              e.preventDefault();
              run(
                () =>
                  updateDriver(driver.id, {
                    vehicle_number: number.trim(),
                    vehicle_model: model.trim(),
                  }),
                tr("Автомобиль обновлён."),
              );
            }}
          >
            <h2 id={`driver-edit-${driver.id}`}><CarFront size={20} aria-hidden="true"/>{tr(" Автомобиль водителя")}</h2>
            <p>{tr(driver.employee_name)}</p>
            {tr(error && (
              <div role="alert" className={s.error}>
                {tr(error)}
              </div>
            ))}
            <div className={s.grid}>
              <label className={s.field}>{tr("Номер автомобиля")}<VehicleNumberInput
                  required
                  value={number}
                  onChange={(e) => setNumber(e.target.value.toUpperCase())}
                />
              </label>
              <label className={s.field}>{tr("Модель автомобиля")}<input
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                />
              </label>
            </div>
            <div style={{ marginTop: 16 }}>
              <DriverInfoCard driver={driver} />
            </div>
            <div className={s.footer}>
              <button
                type="button"
                className={s.button}
                disabled={busy}
                onClick={() => setEditing(false)}
              ><X size={14} aria-hidden="true"/>{tr("Отмена")}</button>
              <button className={s.primary} disabled={busy || !number.trim()}><Save size={14} aria-hidden="true"/>{tr("Сохранить")}</button>
            </div>
          </form>
        </div>
      ))}
    </div>
  );
}
